import {surveyOptions, surveyQuestions} from "../../../widgets/ApplicationSurvey/constant";
import {timestampToDate} from "../../../utils/date";
import {ALL_REGIONS} from "../../../designSystem/components/AddressInput/AddressInput";
import {CATEGORIES_MAPPING} from "../../../utils/constants";

const DEFAULT_SCORE_VALUE = {
  satisfaction1: 0,
  satisfaction2: 0,
  satisfaction3: 0,
  satisfaction4: 0,
}

const AGE_GROUPS = [
  {label: '18歲或以下', min: 0, max: 18},
  {label: '19-25歲', min: 19, max: 25},
  {label: '26-35歲', min: 26, max: 35},
  {label: '36-45歲', min: 36, max: 45},
  {label: '46-55歲', min: 46, max: 55},
  {label: '56-64歲', min: 56, max: 64},
  {label: '65-74歲', min: 65, max: 74},
  {label: '75-84歲', min: 75, max: 84},
  {label: '85歲或以上', min: 85, max: Infinity},
];

const getAcceptedDays = (createdAt, acceptedAt) => {
  if (!createdAt || !acceptedAt) return null;

  const created = timestampToDate(createdAt);
  const accepted = timestampToDate(acceptedAt)

  const diffMs = accepted.getTime() - created.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  return diffDays
};

const getDaysBetweenTimestampAndIso = (createAt, contactTimeStr) => {
  if (!createAt || !contactTimeStr) return null;

  const createdDate = timestampToDate(createAt);
  const contactDate = new Date(contactTimeStr);

  const diffMs = contactDate.getTime() - createdDate.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);

  return diffDays
};

export const calculateSatisfaction = (cases) => {
  const score = { ...DEFAULT_SCORE_VALUE }
  const validCount = { ...DEFAULT_SCORE_VALUE }

  cases.forEach(c => {
    surveyQuestions.forEach(surveyQuestion => {
      if (c.surveyResponses?.[surveyQuestion.id]) {
        const scoreOfQuestion = surveyOptions.indexOf(c.surveyResponses?.[surveyQuestion.id])
        if (scoreOfQuestion >= 1) {
          score[surveyQuestion.id] += scoreOfQuestion
          validCount[surveyQuestion.id] += 1
        }
      }
    })
  })

  const newAverage = {
    satisfaction1:
      validCount.satisfaction1 > 0
        ? score.satisfaction1 / validCount.satisfaction1
        : 0,
    satisfaction2:
      validCount.satisfaction2 > 0
        ? score.satisfaction2 / validCount.satisfaction2
        : 0,
    satisfaction3:
      validCount.satisfaction3 > 0
        ? score.satisfaction3 / validCount.satisfaction3
        : 0,
    satisfaction4:
      validCount.satisfaction4 > 0
        ? score.satisfaction4 / validCount.satisfaction4
        : 0,
  };
  const tempAverageScoreArray: { question: string; count: number }[] = Object.entries(newAverage).map(
    ([question, count]) => ({
      question: surveyQuestions.find(q => q.id === question)?.text || '',
      count: count as number,
    })
  );
  const tempOverallAverage = Object.values(newAverage).reduce((acc, curr) => acc + curr, 0) / Object.keys(newAverage).length;
  return { tempAverageScoreArray, tempOverallAverage }
}

export const countByCategory = (cases) => {
  const categoryCount = cases.reduce((acc, curr) => {
    const category = curr.category || '未知';
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  const categoryCountArray: { category: string; count: number }[] = Object.entries(categoryCount).map(
    ([category, count]) => ({
      category: CATEGORIES_MAPPING[category] || category,
      count: count as number,
    })
  );

  return categoryCountArray
}

export const calculateTimeElapsed = (cases) => {
  const timeElapsed = {
    acceptedDays: {validCount: 0, numberOfDays: 0, average: 0},
    contactDays: {validCount: 0, numberOfDays: 0, average: 0},
    serviceDays: {validCount: 0, numberOfDays: 0, average: 0},
  };

  cases.forEach(c => {
    const acceptedDays = (getAcceptedDays(c.createAt, c.acceptedAt))
    if (timeElapsed) {
      timeElapsed.acceptedDays.validCount += 1
      timeElapsed.acceptedDays.numberOfDays += acceptedDays
    }

    const contactDays = getDaysBetweenTimestampAndIso(c.createAt, c.report?.contactTime)
    if (contactDays) {
      timeElapsed.contactDays.validCount += 1
      timeElapsed.contactDays.numberOfDays += contactDays
    }

    const serviceDays = getDaysBetweenTimestampAndIso(c.createAt, c.report?.serviceTime)
    if (serviceDays) {
      timeElapsed.serviceDays.validCount += 1
      timeElapsed.serviceDays.numberOfDays += serviceDays
    }
  })

  return {
    tempAverageAcceptedDays: timeElapsed.acceptedDays.validCount > 0
      ? timeElapsed.acceptedDays.numberOfDays / timeElapsed.acceptedDays.validCount
      : 0,
    tempAverageContactDays: timeElapsed.contactDays.validCount > 0
      ? timeElapsed.contactDays.numberOfDays / timeElapsed.contactDays.validCount
      : 0,
    tempAverageServiceDays: timeElapsed.serviceDays.validCount > 0
      ? timeElapsed.serviceDays.numberOfDays / timeElapsed.serviceDays.validCount
      : 0
  }
}

const calculateAge = (birthdayStr: string): number => {
  const birthday = new Date(birthdayStr);
  const today = new Date();
  let age = today.getFullYear() - birthday.getFullYear();
  const m = today.getMonth() - birthday.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthday.getDate())) {
    age--;
  }
  return age;
};

export const countByGender = (cases) => {
  const genderCount = cases.reduce((acc, curr) => {
    const gender = curr.patientGender || '未知';
    acc[gender] = (acc[gender] || 0) + 1;
    return acc;
  }, {});

  const genderCountArray: { gender: string; count: number }[] = Object.entries(genderCount).map(
    ([gender, count]) => ({
      gender,
      count: count as number,
    })
  );

  return genderCountArray
}

export const countByAge = (cases) => {
  const ageCount: Record<string, number> = {};
  cases.forEach(c => {
    const birthday = c.patientBirthday;
    if (!birthday) return;

    const age = calculateAge(birthday);

    const group = AGE_GROUPS.find(g => age >= g.min && age <= g.max);
    const label = group ? group.label : '未知';

    ageCount[label] = (ageCount[label] || 0) + 1;
  });

  return AGE_GROUPS.map(({label}) => ({
    label,
    count: ageCount[label] || 0,
  }));
}

export const countByRegion = (cases) => {
  const regionCount = cases.reduce((acc, curr) => {
    const region = curr.patientRegion
      ? curr.patientRegion.replace('屏東縣', '')
      : '未知';

    acc[region] = (acc[region] || 0) + 1;
    return acc;
  }, {});

  return ALL_REGIONS.map((regionName) => ({
    region: regionName,
    count: regionCount[regionName] || 0,
  }));
}
